import React, { useState } from 'react';
import RuleForm from './RuleForm';

const PARAM_CONFIG = {
  temp:        { label: 'Temp',      unit: '°C',   priority: 1,  isBoolean: false },
  co2:         { label: 'CO₂',       unit: ' ppm',  priority: 2,  isBoolean: false },
  hum:         { label: 'Humidity',  unit: '%',    priority: 3,  isBoolean: false },
  battery:     { label: 'Battery',   unit: '%',    priority: 4,  isBoolean: false },
  output:      { label: 'Output',    unit: '',     priority: 5,  isBoolean: true  },
  relay:       { label: 'Relay',     unit: '',     priority: 5,  isBoolean: true  },
  input:       { label: 'Input',     unit: '',     priority: 6,  isBoolean: true  },
  power:       { label: 'Power',     unit: ' W',   priority: 7,  isBoolean: false },
  voltage:     { label: 'Voltage',   unit: ' V',   priority: 8,  isBoolean: false },
  current:     { label: 'Current',   unit: ' A',   priority: 9,  isBoolean: false },
  energy:      { label: 'Energy',    unit: ' kWh', priority: 10, isBoolean: false },
  relay_temp:  { label: 'Relay Tmp', unit: '°C',   priority: 11, isBoolean: false },
};

const PLATFORM_LABELS = {
  shelly:    'Shelly',
  netatmo:   'Netatmo',
  airthings: 'Airthings',
  sensibo:   'Sensibo',
  daikin:    'Daikin',
  ebeco:     'Ebeco',
  goe:       'Go-e',
  danfoss:   'Danfoss',
};

const MODE_LABELS = {
  sensor:     'Sensor',
  relay:      'Relay',
  thermostat: 'Thermostat',
  charger:    'Charger',
  plug:       'Plug',
  shutter:    'Shutter',
};

function formatAge(ageMinutes) {
  if (ageMinutes == null) return null;
  const m = Math.round(ageMinutes);
  if (m < 15) return '<15min';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatParamValue(paramName, value) {
  if (value == null) return '—';
  const config = PARAM_CONFIG[paramName];
  if (!config) return String(value);
  if (config.isBoolean) return value ? 'On' : 'Off';
  return `${value}${config.unit}`;
}

function getConnectivityColor(rate) {
  if (rate == null) return '#ccc';
  if (rate >= 0.9) return '#28a745';
  if (rate >= 0.5) return '#ffc107';
  return '#dc3545';
}

function formatPercent(rate) {
  if (rate == null) return '—';
  return `${Math.round(rate * 100)}%`;
}

/**
 * DeviceCard - Expandable card showing device status and details
 *
 * Props:
 *   - device: device object from DeviceData
 *   - activeAlerts: array of active alerts for this device
 *   - notes: string (installation notes, from device.notes)
 *   - onSaveNotes: function(deviceId, notes) - saves to DeviceData
 *   - deviceOverride: object from configs.device_overrides[deviceId]
 *   - onSaveOverride: function(deviceId, overrideChanges) - saves to MonitoringControl_configs
 *   - systemAlertConfigs: system_alerts config object
 */
const SEVERITY_COLORS = {
  critical: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',
};

const OPERATOR_LABELS = { lt: '<', lte: '<=', gt: '>', gte: '>=' };

function formatConditionSummary(rule) {
  if (!rule.condition) return '';
  const op = OPERATOR_LABELS[rule.condition.operator] || rule.condition.operator;
  const param = rule.parameter || '';
  const threshold = rule.condition.threshold;
  if (rule.condition.type === 'average') {
    return `${param} ${op} ${threshold} avg ${rule.condition.period_hours}h`;
  }
  return `${param} ${op} ${threshold}`;
}

const DeviceCard = ({
  device,
  activeAlerts,
  notes,
  onSaveNotes,
  deviceOverride,
  onSaveOverride,
  systemAlertConfigs,
  allRules,
  allDevices,
  onSaveRules,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(notes || '');
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const deviceId = device.device_id || device.id;
  const alertCount = activeAlerts ? activeAlerts.length : 0;
  const hasAlerts = alertCount > 0;
  const ageMinutes = device.age_minutes;
  const ageText = formatAge(ageMinutes);

  const isOffline = device.error === true;
  // Don't flag stale for devices where last_seen is not a reliable connectivity indicator
  const isStale = !isOffline && !device.offline_monitoring_disabled && ageMinutes != null && ageMinutes > 120;

  // Sort monitored parameters by priority; exclude unknown/non-display params
  const monitoredParams = (device.monitored_parameters || [])
    .filter(p => PARAM_CONFIG[p])
    .sort((a, b) => (PARAM_CONFIG[a].priority) - (PARAM_CONFIG[b].priority));

  const compactParams = monitoredParams.slice(0, 3);

  // Show 24h stats only when device has non-boolean sensor parameters with data
  const sensorParams = monitoredParams.filter(p => !PARAM_CONFIG[p]?.isBoolean);
  const hasStatsData = sensorParams.some(p =>
    device[`${p}_min`] != null || device[`${p}_avg`] != null || device[p] != null
  );
  const showStats = hasStatsData;

  // Connectivity
  const rate24h = device.success_rate_24h != null ? parseFloat(device.success_rate_24h) : null;
  const rate3d  = device.success_rate_3d  != null ? parseFloat(device.success_rate_3d)  : null;
  const rate7d  = device.success_rate_7d  != null ? parseFloat(device.success_rate_7d)  : null;
  const trend = device.success_rate_trend;
  const trendLabel = trend === 'improving' ? '▲' : trend === 'degrading' ? '▼' : null;
  const trendClass  = trend === 'improving' ? 'trend-up' : 'trend-down';

  // Offline monitoring settings
  const offlineConfig = systemAlertConfigs?.offline || {};
  const defaultThreshold = offlineConfig.sensor_threshold_minutes ?? 360;

  // Effective state from DeviceData (what MonitoringControl last computed)
  const offlineMonitoringDisabled = device.offline_monitoring_disabled === true;

  // Editable override values - initialise from deviceOverride config
  const [offlineEnabled, setOfflineEnabled] = useState(
    deviceOverride?.offline_monitoring !== false
  );
  const [thresholdValue, setThresholdValue] = useState(
    deviceOverride?.sensor_threshold_minutes ?? device._sensor_threshold_override ?? defaultThreshold
  );

  const handleOfflineToggle = (e) => {
    const enabled = e.target.checked;
    setOfflineEnabled(enabled);
    if (onSaveOverride) {
      onSaveOverride(deviceId, { offline_monitoring: enabled });
    }
  };

  const handleThresholdBlur = () => {
    const val = parseInt(thresholdValue, 10);
    if (!isNaN(val) && val > 0 && onSaveOverride) {
      onSaveOverride(deviceId, { sensor_threshold_minutes: val });
    }
  };

  // Rules
  const deviceRules = (allRules || []).filter(r => r.device_id === deviceId);
  const allRulesIds = (allRules || []).map(r => r.id);

  const handleRuleToggle = (ruleId) => {
    const updated = (allRules || []).map(r => r.id === ruleId ? { ...r, enabled: !r.enabled } : r);
    if (onSaveRules) onSaveRules(updated);
  };

  const handleRuleDelete = (ruleId) => {
    const updated = (allRules || []).filter(r => r.id !== ruleId);
    if (onSaveRules) onSaveRules(updated);
  };

  const handleRuleSave = (rule) => {
    let updated;
    if (editingRule) {
      updated = (allRules || []).map(r => r.id === editingRule.id ? rule : r);
    } else {
      updated = [...(allRules || []), rule];
    }
    if (onSaveRules) onSaveRules(updated);
    setEditingRule(null);
    setShowRuleForm(false);
  };

  // Notes
  const handleNotesSave = () => {
    if (onSaveNotes) onSaveNotes(deviceId, notesValue);
    setEditingNotes(false);
  };

  const handleNotesCancel = () => {
    setNotesValue(notes || '');
    setEditingNotes(false);
  };

  return (
    <>
    <div className={`device-card ${isOffline ? 'device-offline' : ''} ${hasAlerts ? 'device-has-alerts' : ''}`}>
      {/* Compact header */}
      <div
        className="device-card-header"
        onClick={() => setExpanded(!expanded)}
        role="button"
        aria-expanded={expanded}
      >
        <div className="device-card-header-left">
          <span className={`device-status-dot ${isOffline ? 'dot-offline' : isStale ? 'dot-stale' : 'dot-online'}`} />
          <span className="device-name">{device.name || deviceId}</span>
          <span className="device-platform-badge">
            {PLATFORM_LABELS[device.platform] || device.platform || '—'}
          </span>
          {device.mode && (
            <span className="device-mode-badge">
              {MODE_LABELS[device.mode] || device.mode}
            </span>
          )}
        </div>

        <div className="device-card-header-right">
          {hasAlerts && (
            <span className="device-alert-badge">⚠ {alertCount}</span>
          )}
          {isOffline ? (
            <span className="device-age age-offline">
              {device.last_seen ? `offline · ${device.last_seen.slice(11, 16)}` : 'offline'}
            </span>
          ) : ageText && !device.offline_monitoring_disabled && (
            <span className={`device-age ${isStale ? 'age-stale' : ''}`}>
              {ageText}
            </span>
          )}
          <span className="device-expand-icon">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Compact values row */}
      {compactParams.length > 0 && (
        <div className="device-values-row" onClick={() => setExpanded(!expanded)}>
          {compactParams.map(param => (
            <span key={param} className="device-value-chip">
              <span className="chip-label">{PARAM_CONFIG[param]?.label || param}:</span>
              <span className="chip-value">{formatParamValue(param, device[param])}</span>
            </span>
          ))}
        </div>
      )}

      {/* Active alert tags */}
      {hasAlerts && (
        <div className="device-alert-inline" onClick={() => setExpanded(!expanded)}>
          {activeAlerts.map((a, i) => (
            <span key={i} className={`device-alert-tag severity-${a.severity || 'warning'}`}>
              {(a.alert_type || '').replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}

      {/* Expanded section */}
      {expanded && (
        <div className="device-card-expanded">
          <hr className="device-card-divider" />

          {/* Offline / stale status detail */}
          {(isOffline || isStale) && (
            <div className="device-offline-detail">
              {device.last_seen && (
                <span className="offline-last-seen">Last seen: {device.last_seen}</span>
              )}
              {device.error_message && (
                <span className="offline-error-msg">{device.error_message}</span>
              )}
              {isOffline && !device.error_message && (
                <span className="offline-error-msg">Device is not responding</span>
              )}
            </div>
          )}

          {/* Network status summary - virtual devices only */}
          {device.virtual && (() => {
            // Compute degraded/failing devices from allDevices using same thresholds as backend
            const physicalDevices = (allDevices || []).filter(d => !d.virtual && d.device_id !== 'system_network' && d.id !== 'system_network');
            const degradedDevices = physicalDevices.filter(d => {
              const r = d.success_rate_24h != null ? parseFloat(d.success_rate_24h) : null;
              return r != null && r < 0.95 && r >= 0.70;
            });
            const failingDevices = physicalDevices.filter(d => {
              const r = d.success_rate_24h != null ? parseFloat(d.success_rate_24h) : null;
              return r != null && r < 0.70;
            });
            return (
              <div className="device-section">
                <h5 className="device-section-title">Network Status</h5>
                <div className="connectivity-inline">
                  {device.devices_total != null && (
                    <span className="connectivity-item">
                      <span className="connectivity-period">Online</span>
                      <span className="connectivity-value" style={{ color: '#333' }}>
                        {device.devices_online ?? '—'} / {device.devices_total}
                      </span>
                    </span>
                  )}
                  {device.network_success_rate != null && (
                    <span className="connectivity-item">
                      <span className="connectivity-period">Avg 24h success</span>
                      <span className="connectivity-value" style={{ color: getConnectivityColor(device.network_success_rate) }}>
                        {formatPercent(device.network_success_rate)}
                      </span>
                    </span>
                  )}
                </div>
                {(degradedDevices.length > 0 || failingDevices.length > 0) && (
                  <div className="network-issue-list">
                    {failingDevices.map(d => (
                      <div key={d.device_id || d.id} className="network-issue-row network-issue-failing">
                        <span className="network-issue-label">Failing</span>
                        <span className="network-issue-name">{d.name}</span>
                        <span className="network-issue-rate">{formatPercent(parseFloat(d.success_rate_24h))}</span>
                      </div>
                    ))}
                    {degradedDevices.map(d => (
                      <div key={d.device_id || d.id} className="network-issue-row network-issue-degraded">
                        <span className="network-issue-label">Degraded</span>
                        <span className="network-issue-name">{d.name}</span>
                        <span className="network-issue-rate">{formatPercent(parseFloat(d.success_rate_24h))}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Stats - sensors only; min/max from 4h hourly buckets */}
          {showStats && (
            <div className="device-section">
              <h5 className="device-section-title">Statistics</h5>
              <table className="device-stats-table">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Current</th>
                    <th>Min (4h)</th>
                    <th>Avg (4h)</th>
                    <th>Max (4h)</th>
                  </tr>
                </thead>
                <tbody>
                  {sensorParams.map(param => {
                    const current = device[param];
                    const min = device[`${param}_min`];
                    const avg = device[`${param}_avg`];
                    const max = device[`${param}_max`];
                    if (current == null && min == null) return null;
                    return (
                      <tr key={param}>
                        <td>{PARAM_CONFIG[param]?.label || param}</td>
                        <td>{formatParamValue(param, current)}</td>
                        <td>{min != null ? formatParamValue(param, min) : '—'}</td>
                        <td>{avg != null ? formatParamValue(param, avg) : '—'}</td>
                        <td>{max != null ? formatParamValue(param, max) : '—'}</td>
                      </tr>
                    );
                  }).filter(Boolean)}
                </tbody>
              </table>
            </div>
          )}

          {/* Connectivity - inline color-coded; not shown for devices with unreliable last_seen */}
          {rate24h != null && !offlineMonitoringDisabled && (
            <div className="device-section">
              <h5 className="device-section-title">Connectivity</h5>
              <div className="connectivity-inline">
                <span className="connectivity-item">
                  <span className="connectivity-period">24h</span>
                  <span className="connectivity-value" style={{ color: getConnectivityColor(rate24h) }}>
                    {formatPercent(rate24h)}
                  </span>
                </span>
                {rate3d != null && (
                  <span className="connectivity-item">
                    <span className="connectivity-period">3d</span>
                    <span className="connectivity-value" style={{ color: getConnectivityColor(rate3d) }}>
                      {formatPercent(rate3d)}
                    </span>
                  </span>
                )}
                {rate7d != null && (
                  <span className="connectivity-item">
                    <span className="connectivity-period">7d</span>
                    <span className="connectivity-value" style={{ color: getConnectivityColor(rate7d) }}>
                      {formatPercent(rate7d)}
                    </span>
                  </span>
                )}
                {trendLabel && (
                  <span className={`connectivity-trend-inline ${trendClass}`}>{trendLabel}</span>
                )}
              </div>
            </div>
          )}

          {/* Alert Settings - not shown for virtual devices */}
          {!device.virtual && <div className="device-section">
            <h5 className="device-section-title">Alert Settings</h5>
            <div className="device-alert-settings">
              <div className="alert-setting-row">
                <span className="setting-label">Offline detection</span>
                {offlineMonitoringDisabled ? (
                  <span className="setting-unavailable">Not available for this device type</span>
                ) : (
                  <>
                    <label className="device-toggle" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={offlineEnabled}
                        onChange={handleOfflineToggle}
                      />
                      <span className="device-toggle-slider" />
                    </label>
                    {offlineEnabled && (
                      <span className="threshold-inline">
                        after
                        <input
                          type="number"
                          className="threshold-input"
                          value={thresholdValue}
                          onChange={e => setThresholdValue(e.target.value)}
                          onBlur={handleThresholdBlur}
                          onClick={e => e.stopPropagation()}
                          min={15}
                          step={15}
                        />
                        min
                      </span>
                    )}
                    {!offlineEnabled && (
                      <span className="setting-disabled-label">disabled</span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>}

          {/* Rules - sensors only */}
          {(device.mode === 'sensor' || ['netatmo', 'airthings'].includes(device.platform)) && (
          <div className="device-section">
            <h5 className="device-section-title">
              Rules
              <button
                className="notes-edit-btn"
                onClick={(e) => { e.stopPropagation(); setEditingRule(null); setShowRuleForm(true); }}
              >
                + Add
              </button>
            </h5>
            {deviceRules.length === 0 ? (
              <p className="notes-placeholder">No rules. Click + Add to create one.</p>
            ) : (
              <div className="device-rules-list">
                {deviceRules.map(rule => {
                  const activeAlert = activeAlerts && activeAlerts.find(a => a.alert_type === rule.id);
                  return (
                    <div key={rule.id} className="device-rule-row">
                      <label className="rule-row-toggle" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={rule.enabled !== false}
                          onChange={() => handleRuleToggle(rule.id)}
                        />
                        <span className="rule-row-toggle-slider" />
                      </label>
                      <span className={`rule-row-name${rule.enabled === false ? ' rule-disabled' : ''}`}>
                        {rule.name || rule.id}
                      </span>
                      <span className="rule-row-condition">{formatConditionSummary(rule)}</span>
                      {activeAlert && (
                        <span
                          className="rule-alert-dot"
                          style={{ backgroundColor: SEVERITY_COLORS[activeAlert.severity] || SEVERITY_COLORS.warning }}
                        />
                      )}
                      <button
                        className="rule-row-edit"
                        onClick={(e) => { e.stopPropagation(); setEditingRule(rule); setShowRuleForm(true); }}
                      >
                        Edit
                      </button>
                      <button
                        className="rule-row-delete"
                        onClick={(e) => { e.stopPropagation(); handleRuleDelete(rule.id); }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}

          {/* Notes */}
          <div className="device-section">
            <h5 className="device-section-title">
              Notes
              {!editingNotes && (
                <button
                  className="notes-edit-btn"
                  onClick={(e) => { e.stopPropagation(); setEditingNotes(true); }}
                >
                  Edit
                </button>
              )}
            </h5>
            {editingNotes ? (
              <div className="notes-editor">
                <textarea
                  className="notes-textarea"
                  value={notesValue}
                  onChange={e => setNotesValue(e.target.value)}
                  onKeyDown={e => e.key === 'Escape' && handleNotesCancel()}
                  placeholder="Installation notes, location, wiring details..."
                  rows={3}
                  autoFocus
                />
                <div className="notes-actions">
                  <button className="notes-save-btn" onClick={handleNotesSave}>Save</button>
                  <button className="notes-cancel-btn" onClick={handleNotesCancel}>Cancel</button>
                </div>
              </div>
            ) : (
              <p className="notes-text">
                {notesValue || <span className="notes-placeholder">No notes. Click Edit to add installation notes.</span>}
              </p>
            )}
          </div>
        </div>
      )}
    </div>

    {showRuleForm && (
      <RuleForm
        rule={editingRule}
        devices={allDevices || []}
        onSave={handleRuleSave}
        onCancel={() => { setEditingRule(null); setShowRuleForm(false); }}
        existingRuleIds={allRulesIds}
        lockedDeviceId={deviceId}
      />
    )}
    </>
  );
};

export default DeviceCard;
